/**
 * 자막 피드 — 가상 스크롤 + 위쪽 무한 스크롤(§8.3) + 하단 고정.
 *
 * 가상화는 **TanStack Virtual**(§14)을 쓴다. @cupia/ui 도 같은 패키지를
 * dependencies 로 갖지만, **직접 import 하는 쪽이 직접 선언한다** — 남의
 * 전이 의존에 기대는 유령 의존(phantom dependency)은 호이스팅 방식이 바뀌는
 * 순간 깨진다(실제로 pnpm 11 컨테이너 빌드에서 모듈을 못 찾아 터졌다).
 * 버전 범위는 @cupia/ui 와 같게 두어 락파일이 한 벌만 해석하게 한다.
 *
 * 요구 사항:
 *  - 최신 내용이 스크롤 없이 보인다(회의실 프로젝터 용례) → **최신 쪽 끝**에
 *    고정, 단 사용자가 과거를 읽는 중이면 고정을 풀어 방해하지 않는다.
 *  - 과거 쪽 끝에 다다르면 히스토리 페이지를 지연 로드한다(§8.3) — 이때
 *    스크롤 위치를 보정해 읽던 자리가 튀지 않게 한다.
 *  - partial in-place 교체는 리듀서가 보장하므로 여기서는 순서를 건드리지 않는다.
 *
 * **정렬 방향(order)은 순수 표시 관심사다** — 리듀서 상태는 언제나 최신
 * 우선이고 이 컴포넌트가 방향만 정한다. 그래서 위/아래라는 말 대신 "최신 쪽
 * 끝 / 과거 쪽 끝" 으로 쓴다: 방향이 뒤집혀도 규칙이 그대로 성립한다.
 * 자세한 근거는 lib/feed-order.ts 의 주석에 있다.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Spinner } from '@cupia/ui/spinner'
import { Empty } from '@cupia/ui/empty'
import { MessagesSquare } from 'lucide-react'
import type { FeedItem, FinalItem } from '../lib/reducer'
import { DEFAULT_ORDER, type FeedOrder } from '../lib/feed-order'
import { finalSide, paragraphSide, type CardSide } from '../lib/align'
import type { PanelScrollState } from '../lib/split-view'
import { indexMembers } from '../lib/split-rows'
import { CaptionCard } from './caption-card'
import { LiveWaiting, LIVE_WAIT_AFTER_MS } from './live-waiting'
import type { ConnState } from '../lib/transport'

export interface CaptionFeedProps {
  items: FeedItem[]
  src: string
  dst: string
  showSource?: boolean
  /** 원문 가림(시각만 — lib/source-veil.ts). 카드 높이를 바꾸지 않는다. */
  veilSource?: boolean
  /** CaptionCard 로 그대로 전달 — 켜면 추론된 자막이 기본 한 줄로 접힌다. */
  collapseInferred?: boolean
  /** CaptionCard 로 그대로 전달 — 켜면 판정기 자막이 한 줄로 접힌다(별도 축). */
  collapseAssistant?: boolean
  /** 호스트만 — 삭제(§8.4) */
  onDelete?: (item: FinalItem) => void
  /** 화자 ID→이름 맵(speaker.update) — 칩 표기, 소급 반영 */
  speakers?: Record<string, string>
  /** 호스트만 — 화자 칩 클릭으로 이름 지정 */
  onRenameSpeaker?: (spk: string) => void
  /** §8.3 과거 쪽 끝에 다다르면 과거분 로드. false 를 반환하면 더 없음. */
  onLoadMore?: () => Promise<boolean>
  /** 발표 모드에서 카드 간격을 넓힌다 */
  presentation?: boolean
  /**
   * 정렬 방향 — **순수 표시 관심사**다(lib/feed-order.ts 의 주석 참조).
   * 리듀서 상태는 항상 최신 우선이고, 여기서 방향만 정한다.
   */
  order?: FeedOrder
  emptyTitle?: string
  emptyDescription?: string
  /**
   * S25 원문 직역 온디맨드 — 둘 다 있어야 CaptionCard 가 버튼을 그린다.
   * 방 코드 + 현재 Bearer 자격(`auth.current()`, §5.2). 뷰어·호스트 양쪽
   * 화면이 같은 방식으로 넘긴다(엔드포인트 인가가 `_require_viewer` 라
   * 둘 다 허용 범위다).
   */
  room?: string
  viewerKey?: string
  /**
   * 분할 보기 — 이 종류의 항목만 그린다. **미지정이면 오늘과 동일**
   * (통합 타임라인: 정렬 래퍼 · 접힘선 · 문단 흡수 전부 그대로).
   * 지정되면 D2-2 파이프라인 ③④⑤가 적용된다: 'final' 이 포함되면
   * rawItems 에서 고르고(흡수 final 유지), 정렬 래퍼와 접힘선을 끈다.
   * membersByPara/paraOf 는 kinds 와 무관하게 항상 전체 입력에서 계산한다.
   */
  kinds?: readonly FeedItem['kind'][]
  /** aria-live 를 켤 것인가 — 분할 시 좌측만 true(D6-2). 기본 true. */
  live?: boolean
  /**
   * 연결 상태(lib/transport.ts) — "새 자막을 기다리는 중" 표시 조건에 쓴다
   * (TASK_live_waiting_indicator.md). 미지정이면 연결 상태 체크를 생략하고
   * live 패널이면 표시한다(옵션 — 안 넘기면 기존 동작 불변).
   */
  conn?: ConnState
  /**
   * D7 — 언마운트를 넘어 읽던 자리를 보존한다. 미지정이면 오늘과 동일
   * (복원 없음, 최초 마운트는 항상 최신 끝).
   */
  scrollState?: {
    initial: PanelScrollState | null
    onSave: (state: PanelScrollState) => void
    /**
     * D7 기준선 보존(codex 지적 #1, fix2) — `onSave` 는 스크롤/언마운트
     * 시점에만 불려 그 사이 도착한 final 로 바뀐 `unseen`/`seenSeq` 를
     * 반영하지 못한다. `captureLiveState`(split-feed.tsx)가 전환 **직전**
     * 이 콜백을 동기 호출해 지금 이 순간의 진짜 값을 읽는다.
     *
     * 브레이크포인트 전환 앵커 보존(P2, codex fix3) — `unseen`/`seenSeq`
     * 뿐 아니라 `anchorId`/`anchorOffset`/`atNewest` 도 이 스냅샷에
     * 실어 둔다. `captureLiveState` 가 DOM 을 다시 재는 대신 이 값을
     * 그대로 쓰게 하기 위해서다 — 리사이즈로 줄바꿈이 달라진 뒤 DOM 을
     * 재면 다른 카드가 "맨 위" 로 잡힌다(전환 이전 스크롤 시점의 진짜
     * 위치가 아니다).
     */
    getSnapshot?: () => { unseen: number; seenSeq: number } & Pick<
      PanelScrollState,
      'anchorId' | 'anchorOffset' | 'atNewest'
    >
  }
}

/** 가상화 추정 높이 — 실제 높이는 measureElement 가 잡는다. */
function estimateSize(presentation: boolean): number {
  return presentation ? 190 : 110
}

/**
 * 통합 대기 접힘선 — 렌더 전용 항목(리듀서 상태가 아니다).
 * "여기 위는 확정 문단, 이 너머는 아직 창이 안 닫힌 실시간" 의 경계다.
 */
interface FoldItem {
  kind: 'fold'
  id: 'fold-divider'
  seq: null
  utt: null
}

const FOLD_ITEM: FoldItem = {
  kind: 'fold',
  id: 'fold-divider',
  seq: null,
  utt: null,
}

/**
 * 이전 회의 이어하기(S23) — 시딩된(prev=true) 항목과 이번 회의 항목의
 * 경계에 선다. 렌더 전용, 최대 1개.
 */
interface PrevDividerItem {
  kind: 'prev-divider'
  id: 'prev-divider'
  seq: null
  utt: null
}

const PREV_DIVIDER_ITEM: PrevDividerItem = {
  kind: 'prev-divider',
  id: 'prev-divider',
  seq: null,
  utt: null,
}

type RenderItem = FeedItem | FoldItem | PrevDividerItem

/**
 * 정렬 행(S21-b) — 확정 카드·문단만 좌/우 정렬을 받는다.
 * 진행 중(partial)·gap 은 전폭 그대로다: partial 은 방향 판정(passthrough)
 * 이 아직 없고, gap 은 카드가 아니라 구간 마커다.
 */
function sideOf(item: RenderItem, dst: string): CardSide | null {
  if (item.kind === 'final') return finalSide(item.data)
  if (item.kind === 'paragraph') return paragraphSide(item.data, dst)
  return null
}

export function CaptionFeed({
  items: rawItems,
  src,
  dst,
  showSource = true,
  veilSource = false,
  collapseInferred,
  collapseAssistant,
  onDelete,
  speakers,
  onRenameSpeaker,
  onLoadMore,
  presentation = false,
  order = DEFAULT_ORDER,
  emptyTitle = '아직 자막이 없습니다',
  emptyDescription = '발화가 감지되면 여기에 실시간으로 표시됩니다.',
  room,
  viewerKey,
  kinds,
  live = true,
  conn,
  scrollState,
}: CaptionFeedProps): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null)

  /**
   * 장문 문단 통합(record.paragraph) — 통합된 final 은 최상위 피드에서
   * 숨기고 자기 문단 카드의 아코디언 내용(members)으로 넘긴다. 문단이
   * 하나도 없으면 이 단계는 원본을 **항목 그대로** 통과시킨다 — 플래그
   * OFF 방의 DOM 은 오늘과 동일하다.
   */
  const { visible, membersByPara, paraOf } = useMemo(
    // 계산 본문은 `lib/split-rows.ts` 의 `indexMembers` 로 추출돼 있다
    // (SPLITVIEW_ROWS_DESIGN D4-2) — 행 정렬(`SplitRows`)이 같은 역색인을
    // 봐야 하므로 복제가 아니라 한 곳에서 읽는다. 동작은 불변이다.
    () => indexMembers(rawItems),
    [rawItems],
  )

  /** 스크롤 앵커 폴백용 — 앵커 카드가 문단으로 흡수됐을 때 그 문단을 찾는다. */
  const paraOfRef = useRef(paraOf)
  paraOfRef.current = paraOf

  /**
   * D2-2 ③ 원천 선택 + ④ 종류 필터.
   * `kinds` 미지정 → `visible`(기존 경로 그대로, 통과).
   * `kinds` 에 'final' 포함 → `rawItems`(흡수 final 유지, D4).
   * 그 외(지정됐지만 'final' 미포함) → `visible`.
   */
  const filtered = useMemo<FeedItem[]>(() => {
    if (!kinds) return visible
    const source = kinds.includes('final') ? rawItems : visible
    return source.filter((it) => kinds.includes(it.kind))
  }, [kinds, rawItems, visible])

  /**
   * 화면 순서 — ④의 결과 배열(kinds 미지정이면 visible)을 방향에 맞춰 그린다.
   * 접힘선은 **최신 쪽의 첫 문단 직전**(= 실시간 꼬리와 문단 구역 사이)에
   * 끼운다. 문단이 없거나 문단보다 최신인 항목이 없으면 끼우지 않는다 —
   * 경계가 없는 곳에 경계선을 그리면 소음이다. **접힘선은 `kinds` 지정 시
   * 억제한다**(D2-4) — 그 경계가 분할에서는 패널 경계 자체이기 때문이다.
   * 이전 회의 구분선은 두 패널 모두에서 유지한다(D2-5).
   */
  const items = useMemo<RenderItem[]>(() => {
    const foldAt = kinds ? -1 : filtered.findIndex((it) => it.kind === 'paragraph')
    // 이전 회의 경계 — filtered 는 최신 우선이라 prev=true(이전 회의, 더
    // 오래됨) 항목은 배열 끝쪽에 연속으로 몰린다. 첫 prev=true 항목 바로
    // 앞이 경계다. 전부 prev 이거나(=0) 전부 새 항목(=-1)이면 경계가 없다.
    const prevDividerAt = filtered.findIndex(
      (it) => 'prev' in it && it.prev === true,
    )
    const points: Array<{ at: number; item: RenderItem }> = []
    if (foldAt > 0) points.push({ at: foldAt, item: FOLD_ITEM })
    if (prevDividerAt > 0) points.push({ at: prevDividerAt, item: PREV_DIVIDER_ITEM })
    points.sort((a, b) => a.at - b.at)
    let list: RenderItem[] = filtered
    if (points.length > 0) {
      list = []
      let cursor = 0
      for (const p of points) {
        list.push(...filtered.slice(cursor, p.at), p.item)
        cursor = p.at
      }
      list.push(...filtered.slice(cursor))
    }
    return order === 'newest-first' ? list : [...list].reverse()
  }, [filtered, kinds, order])

  /** 최신이 붙는 쪽이 위인가 아래인가. */
  const newestAtTop = order === 'newest-first'

  /**
   * "최신을 보고 있다" — 최신 우선이면 맨 위, 아니면 맨 아래를 뜻한다.
   * 이 상태일 때만 새 자막을 따라 붙어 있는다(§14 프로젝터 용례).
   *
   * D7-5 — `scrollState.initial` 이 있으면(SplitFeed 가 보존해 둔 상태)
   * 그 값으로 초기화한다. 없으면(통합 모드·최초 마운트) 오늘과 동일하게
   * 항상 최신 끝에서 시작한다.
   */
  const [atNewest, setAtNewest] = useState(() => scrollState?.initial?.atNewest ?? true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [exhausted, setExhausted] = useState(false)
  /**
   * 과거를 읽는 동안 도착한 **확정 자막** 수 — 최신 쪽으로 돌아가는 버튼에
   * 싣는다. partial 은 세지 않는다(자라는 중인 한 건이 여러 번 세어진다).
   */
  const [unseen, setUnseen] = useState(() => scrollState?.initial?.unseen ?? 0)

  /**
   * "새 자막을 기다리는 중"(TASK_live_waiting_indicator.md) — live 패널이고
   * 연결이 살아 있는데 최신 자막 이후 `LIVE_WAIT_AFTER_MS` 가 지나면 켠다.
   * `filtered` 는 항상 최신 우선(리듀서 상태 — order 와 무관)이므로
   * `filtered[0]` 이 실제 최신 항목이다(fold/prev-divider 는 렌더 전용이라
   * 여기서 안 본다). 최신 항목의 id 가 바뀌면(=새 자막 도착) 타이머를
   * 재시작해 즉시 숨긴다.
   */
  const latestId = filtered[0]?.id ?? null
  const liveActive = live !== false && (conn === undefined || conn === 'active')
  const [showWaiting, setShowWaiting] = useState(false)
  useEffect(() => {
    setShowWaiting(false)
    if (!liveActive || latestId === null) return
    const timer = setTimeout(() => setShowWaiting(true), LIVE_WAIT_AFTER_MS)
    return () => clearTimeout(timer)
  }, [liveActive, latestId])

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize(presentation),
    overscan: 8,
    getItemKey: (index) => items[index]?.id ?? index,
  })

  /* ── 최신 고정: 최신 쪽 끝이 스크롤 없이 보여야 한다(§14 회의실
       프로젝터 용례). 방향에 따라 그 끝이 위이기도 아래이기도 하다. ──── */
  const stickToNewest = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = newestAtTop ? 0 : el.scrollHeight - el.clientHeight
  }, [newestAtTop])

  /* ── 스크롤 앵커링(실측 결함 수정) ──────────────────────────────────────
   *
   * 증상: 과거를 읽으려 중간까지 내려 둔 상태에서 새 자막이 도착하면 읽던
   * 자리가 발밑에서 밀려났다(실측 Δ82px/2건, 연속 5건이면 Δ205px 누적).
   *
   * 원인은 **가변 높이 + 맨 앞 삽입**의 조합이다. 최신 우선 정렬이라 새 카드는
   * index 0 에 들어가고, measureElement 가 그 카드의 실제 높이를 확정하는
   * 순간 뒤따르는 모든 항목의 offset 이 그만큼 밀린다. scrollTop 은 그대로인데
   * 콘텐츠가 아래로 흘러내리므로, 화면에서는 읽던 문장이 위로 사라진다.
   *
   * 고치는 방법: **읽고 있던 카드를 화면의 같은 자리에 붙들어 둔다.**
   * 목록이 바뀌기 직전(렌더 중)에 화면 상단에 걸친 카드와 그 카드가 상단에서
   * 얼마나 내려와 있는지를 기억하고, 레이아웃이 끝난 뒤 그 카드가 움직인
   * 만큼 scrollTop 을 되돌린다.
   *
   * 앵커를 **id 로** 잡는 것이 요점이다(index 가 아니라): 맨 앞 삽입은 모든
   * index 를 밀어 버리므로 index 기준 앵커는 애초에 성립하지 않는다.
   *
   * 기준은 **DOM 의 실제 위치**다. 가상화기가 캐시한 start 는 measureElement
   * 가 높이를 확정하기 전의 추정값이라 기준으로 쓸 수 없다.
   *
   * 이 보정은 위쪽 추가(새 자막)·아래쪽 추가(히스토리)·높이 확정(partial 이
   * 자람, 글자 크기 변경)을 **한 가지 기제로** 덮는다 — 경우별 분기를 두면
   * 빠진 경우가 반드시 생긴다(실제로 그랬다).
   *
   * **scrollTop 은 이 보정의 성공 지표가 아니다.** 새 자막은 읽던 자리
   * 위쪽에 삽입되므로 같은 문장을 같은 자리에서 보고 있어도 scrollTop 은
   * 그 높이만큼 정당하게 커진다(실측: 앵커 카드가 -98px 에 그대로 있는 동안
   * scrollTop 은 400→702 로 움직였다). 재야 할 것은 카드의 화면 위치다 —
   * e2e/feed-scroll.spec.ts 가 그렇게 잰다.
   */
  const anchorRef = useRef<{ id: string; offset: number } | null>(
    scrollState?.initial?.anchorId
      ? { id: scrollState.initial.anchorId, offset: scrollState.initial.anchorOffset }
      : null,
  )
  /** 방금의 scrollTop 변화가 **우리 보정** 때문인지 표시한다. */
  const selfScrollRef = useRef(false)
  /** D7-4 — 복원 절차가 아직 안 끝났으면 최초 마운트의 "최신 끝으로" 를 막는다. */
  const restorePendingRef = useRef(scrollState?.initial != null && !scrollState.initial.atNewest)

  /**
   * 지금 화면 맨 위에 걸린 카드와, 그 카드가 뷰포트 상단에서 얼마나
   * 내려와 있는지를 잡는다. 기준은 **DOM 의 실제 위치**다 —
   * 가상화기가 캐시한 start 는 measureElement 가 높이를 확정하기 전의
   * 추정값이라 이 보정의 기준으로 쓸 수 없다(실제로 그걸 기준 삼았더니
   * 같은 이동을 두 번 세어 Δ가 오히려 878px 로 늘었다).
   */
  const captureAnchor = useCallback((): void => {
    const el = scrollRef.current
    if (!el) return
    const top = el.getBoundingClientRect().top
    for (const node of el.querySelectorAll<HTMLElement>('[data-item-id]')) {
      const rect = node.getBoundingClientRect()
      // 뷰포트 상단을 지나쳐 아직 화면에 걸쳐 있는 첫 카드.
      if (rect.bottom > top) {
        const id = node.dataset['itemId']
        if (id !== undefined) anchorRef.current = { id, offset: rect.top - top }
        return
      }
    }
    anchorRef.current = null
  }, [])

  /**
   * 앵커 카드를 원래 있던 화면 위치로 되돌린다 — 그 사이 위쪽에 무엇이
   * 삽입됐든, 어떤 카드의 높이가 확정됐든 읽던 자리는 제자리에 남는다.
   */
  const restoreAnchor = useCallback((): void => {
    const el = scrollRef.current
    const anchor = anchorRef.current
    if (!el || !anchor) return
    let node = el.querySelector<HTMLElement>(
      `[data-item-id="${CSS.escape(anchor.id)}"]`,
    )
    if (!node) {
      // 앵커 카드가 문단으로 흡수돼 최상위 피드에서 사라졌다 — 그 문단
      // 카드를 대리 앵커로 쓴다. 정확히 같은 오프셋은 아니지만(카드들이
      // 문단 하나로 줄었다) 읽던 구역이 화면에 남는다. 이 폴백이 없으면
      // 과거를 읽는 도중의 통합이 스크롤을 잡아챈다(지시의 CRITICAL UX).
      const paraId = paraOfRef.current.get(anchor.id)
      if (paraId) {
        node = el.querySelector<HTMLElement>(
          `[data-item-id="${CSS.escape(paraId)}"]`,
        )
      }
    }
    if (!node) return
    const drift =
      node.getBoundingClientRect().top - el.getBoundingClientRect().top - anchor.offset
    if (drift !== 0) {
      // 우리가 옮기는 스크롤은 **사용자의 스크롤이 아니다** — 표시해 두지
      // 않으면 이 이동이 scroll 이벤트를 깨우고, 핸들러가 밀린 자리를 새
      // 앵커로 잡아 보정을 스스로 무효화한다(실측: Δ가 755px 로 남았다).
      selfScrollRef.current = true
      el.scrollTop += drift
    }
  }, [])

  /**
   * **렌더 중에** 앵커를 잡는다 — 이 시점의 DOM 은 아직 이전 목록이다.
   *
   * 타이밍이 이 보정의 전부다. 레이아웃 이펙트에서 잡으면 이미 새 카드가
   * 들어온 뒤라 "밀린 자리"를 기준으로 삼게 되고, 그러면 되돌릴 것이 없다고
   * 판단해 아무것도 고치지 못한다(실측으로 확인했다 — 보정을 넣었는데도
   * Δ가 그대로였다). 밀리기 **전**의 자리를 기억해야 밀린 만큼을 되돌린다.
   */
  const prevItemsRef = useRef(items)
  if (prevItemsRef.current !== items) {
    prevItemsRef.current = items
    if (!atNewest) captureAnchor()
  }

  /**
   * 새로 확정된 자막 수를 센다 — 최신 쪽에서 멀어져 있는 동안만.
   * seq 는 방 전역 단조 축(§8.1)이라 "본 적 없는 최대 seq" 하나로 충분하다.
   *
   * D7-1 — 통합 모드(`kinds` 미지정)는 이 계산을 오늘 그대로(`rawItems`
   * 분모 · `kind === 'final'` 자격) 유지한다. `kinds` 지정 시(분할 모드)만
   * 그 패널의 ④ 결과 배열(`filtered`)을 분모로 쓰고, 자격 조건을
   * `seq !== null` 로 일반화한다 — 그대로 두면 우측(문단) 패널의 배지가
   * 좌측 실시간 final 을 센다(분모가 rawItems 라서).
   */
  const unseenSource = kinds ? filtered : rawItems
  const maxSeq = useMemo(
    () => unseenSource.reduce((max, it) => (it.seq !== null && it.seq > max ? it.seq : max), 0),
    [unseenSource],
  )
  const seenSeqRef = useRef(scrollState?.initial?.seenSeq ?? maxSeq)
  useEffect(() => {
    if (atNewest) {
      // 최신을 보고 있으면 도착하는 즉시 읽은 것이다.
      seenSeqRef.current = maxSeq
      setUnseen(0)
      return
    }
    const added = unseenSource.filter((it) =>
      kinds ? it.seq !== null && it.seq > seenSeqRef.current : it.kind === 'final' && it.seq !== null && it.seq > seenSeqRef.current,
    ).length
    if (added > 0) setUnseen(added)
  }, [maxSeq, atNewest, unseenSource, kinds])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return

    // 최신 쪽 끝을 보고 있으면 앵커링 대신 최신 고정이 이긴다 — 새 자막을
    // 따라 붙어 있어야 하는 자리다(회의실 프로젝터 용례).
    if (atNewest) {
      stickToNewest()
      anchorRef.current = null
      return
    }
    restoreAnchor()
  })

  /**
   * 정렬 방향이 바뀌면 최신 쪽 끝으로 데려간다.
   *
   * 방향 전환은 목록을 뒤집는 일이라 "읽던 자리"를 그대로 보존한다는 말이
   * 성립하지 않는다(위였던 것이 아래가 된다). 사용자가 방금 한 조작의
   * 결과를 바로 보여 주는 편이 정직하다 — 최신 자막이 곧 그 결과다.
   */
  const prevOrderRef = useRef(order)
  useLayoutEffect(() => {
    if (prevOrderRef.current === order) return
    prevOrderRef.current = order
    anchorRef.current = null
    setAtNewest(true)
    stickToNewest()
  }, [order, stickToNewest])

  /**
   * measureElement 는 ResizeObserver 로 **렌더 이후에** 높이를 확정한다.
   * 그때 위쪽 카드의 높이가 바뀌면 아래가 통째로 밀리므로, 레이아웃 이펙트
   * 한 번으로는 부족하다 — 관측이 끝난 뒤에도 같은 앵커로 한 번 더 되돌린다.
   * (여기서 앵커를 다시 잡지 않는 것이 중요하다: 다시 잡으면 밀린 자리가
   * 새 기준이 되어 보정이 무력화된다.)
   */
  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (atNewest) {
        stickToNewest()
        return
      }
      restoreAnchor()
    })
    for (const node of el.querySelectorAll<HTMLElement>('[data-item-id]')) {
      observer.observe(node)
    }
    return () => observer.disconnect()
  }, [items, atNewest, stickToNewest, restoreAnchor])

  /**
   * D7-4 — 마운트 시 복원 절차(3단계, `scrollState.initial` 이 있을 때만).
   * ① `atNewest === true` 였으면 위 useLayoutEffect 가 이미 stickToNewest 로
   *   끝냈다(앵커가 필요 없다). 여기서는 ②③(과거 쪽 앵커 복원)만 다룬다.
   * ② 렌더 범위 진입 — 저장된 앵커 id 를 items 에서 찾아 그 인덱스로
   *   `virtualizer.scrollToIndex` 를 호출한다. 못 찾으면(그 사이 항목이
   *   밀려났다) 복원을 포기하고 최신 끝으로 간다 — 없는 자리를 흉내내지
   *   않는다.
   * ③ 오프셋 복원 — 다음 프레임에 anchorRef 를 확정 세팅하고 restoreAnchor
   *   를 호출해 DOM 실측 기준으로 화면 위치를 맞춘다.
   */
  const didMountRestoreRef = useRef(false)
  useEffect(() => {
    // 통합 모드(`kinds` 미지정)는 "바이트 동일 실행" 이 계약이다(D4-5) —
    // 이 효과는 분할 모드의 D7-4 복원 절차 전용이므로 여기서 아무 것도
    // 하지 않고 끝낸다(codex 지적 #4). 이 분기가 없으면 `scrollState`
    // 없는 통합 모드에서도 `stickToNewest()` 가 한 번 더 불려, 아래 526줄의
    // 기존 마운트 이펙트와 나란히 도는 새 실행 경로가 생긴다.
    if (!kinds) return
    if (didMountRestoreRef.current) return
    didMountRestoreRef.current = true

    const initial = scrollState?.initial
    if (!initial || initial.atNewest) {
      restorePendingRef.current = false
      stickToNewest()
      return
    }
    const index = items.findIndex((it) => it.id === initial.anchorId)
    if (index < 0) {
      // 앵커 카드를 목록에서 못 찾았다 — 복원을 포기하고 최신 끝으로.
      restorePendingRef.current = false
      anchorRef.current = null
      setAtNewest(true)
      stickToNewest()
      return
    }
    // scrollToIndex 를 다음 프레임으로 미룬다 — 마운트 직후(특히 이
    // 인스턴스가 막 리사이즈로 태어난 경우) 스크롤 엘리먼트의
    // ResizeObserver 측정이 아직 안 끝났을 수 있고, 그 상태에서 스크롤을
    // 옮기면 다음 순간 측정이 갱신되며 가상화기가 스크롤을 0 으로 되돌린다
    // (실측 — 데스크톱 전환 직후 scrollTop 이 2530→0 으로 스스로 리셋됐다).
    // 한 프레임 기다리면 측정이 안정된 뒤에 옮길 수 있다.
    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(index, { align: 'start' })
      requestAnimationFrame(() => {
        anchorRef.current = { id: initial.anchorId as string, offset: initial.anchorOffset }
        restoreAnchor()
        // 폭이 바뀌며(예: 데스크톱→모바일 패널 폭 변화) 카드 줄바꿈이
        // 달라지면 위쪽 카드들의 실제 높이가 이 시점 이후에도 계속
        // 확정된다 — 한 프레임 더 뒤에 같은 앵커로 한 번 더 보정한다
        // (아래 ResizeObserver 도 같은 목적이지만, 카드 자체는 크기가 안
        // 바뀌어도 그 앞 카드들의 확정이 이 프레임 이후에 몰릴 수 있다).
        requestAnimationFrame(() => {
          restoreAnchor()
          restorePendingRef.current = false
        })
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 최초 마운트 시 즉시 최신 쪽 끝으로 — 복원 상태가 없을 때만(D7-4).
  useEffect(() => {
    if (restorePendingRef.current) return
    stickToNewest()
  }, [stickToNewest])

  /**
   * D7-3 — 언마운트 직전(cleanup)에 현재 스크롤 상태를 `SplitFeed` 로
   * 올린다. 최신값을 ref 로 들고 있다가 cleanup 에서 그 ref 를 읽는다 —
   * 클린업 시점의 클로저가 옛 값을 붙들지 않게.
   */
  const liveScrollSnapshotRef = useRef({ atNewest, unseen })
  liveScrollSnapshotRef.current = { atNewest, unseen }

  // D7 기준선 보존(codex 지적 #1, fix2) — `scrollState.getSnapshot` 을 매
  // 렌더 갱신해 둔다(위 ref 와 같은 패턴). SplitFeed 의 captureLiveState 가
  // 전환을 일으키는 이벤트 핸들러 안에서 이 함수를 동기 호출하면, onSave 가
  // 아직 못 올린(스크롤/언마운트 이전) 최신 unseen/seenSeq 를 그대로 읽는다.
  if (scrollState) {
    scrollState.getSnapshot = () => {
      const snap = liveScrollSnapshotRef.current
      return {
        unseen: snap.unseen,
        seenSeq: seenSeqRef.current,
        anchorId: snap.atNewest ? null : (anchorRef.current?.id ?? null),
        anchorOffset: snap.atNewest ? 0 : (anchorRef.current?.offset ?? 0),
        atNewest: snap.atNewest,
      }
    }
  }
  // useLayoutEffect 다: 탭 전환·브레이크포인트 전환은 이 인스턴스의
  // 언마운트와 형제(다른 패널) 인스턴스의 새 마운트가 **같은 커밋**에서
  // 일어난다. 일반 useEffect 클린업은 페인트 이후(비동기)에 돌아 새
  // 인스턴스의 useState 초기화(이미 렌더 단계에서 끝남)보다 늦다 —
  // scrollState.initial 이 항상 한 박자 묵은 값을 받는다. useLayoutEffect
  // 클린업은 커밋 중 동기로 돌아 형제의 레이아웃 이펙트보다 먼저 끝난다.
  useLayoutEffect(() => {
    const onSave = scrollState?.onSave
    if (!onSave) return undefined
    return () => {
      const snap = liveScrollSnapshotRef.current
      onSave({
        anchorId: anchorRef.current?.id ?? null,
        anchorOffset: anchorRef.current?.offset ?? 0,
        atNewest: snap.atNewest,
        unseen: snap.unseen,
        seenSeq: seenSeqRef.current,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── 스크롤 핸들러: 최신 쪽 판정 + **과거 쪽** 무한 스크롤 ─────────── */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    // D7-4 복원이 진행 중일 때는 스크롤 판정을 건너뛴다. virtualizer 의
    // scrollToIndex 가 내부적으로 여러 프레임에 걸쳐 scrollTop 을 만지는
    // 동안(측정이 갱신될 때마다) 중간값에서 native scroll 이벤트가 뜰 수
    // 있고, 그 중간값이 "맨 위"로 보이면 이 판정이 atNewest 를 true 로
    // 잘못 확정해 복원 자체를 무효화한다(실측 — 데스크톱 전환 시 재현).
    if (restorePendingRef.current) return

    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    // "최신을 보고 있다" 의 뜻이 방향에 따라 뒤집힌다.
    const nextAtNewest = newestAtTop ? el.scrollTop < 48 : fromBottom < 48
    setAtNewest(nextAtNewest)
    // 사용자가 스크롤했으면 앵커도 새 자리로 옮긴다 — 옛 앵커를 붙들면
    // 보정이 사용자의 스크롤과 싸운다. 다만 **우리가 옮긴 스크롤**은
    // 사용자의 의도가 아니므로 앵커를 다시 잡지 않는다.
    if (selfScrollRef.current) {
      selfScrollRef.current = false
    } else {
      captureAnchor()
    }
    // D7-5 — SplitFeed 가 언마운트를 기다리지 않고도 "마지막으로 알려진
    // 좋은 위치" 를 갖게 한다. 브레이크포인트 전환은 물리적 리사이즈 이후에만
    // 콜백(matchMedia change)이 오므로, 그 시점에 DOM 을 다시 재서 앵커를
    // 잡으면 이미 새 폭에서 줄바꿈이 달라진 뒤라 다른 카드를 고를 수 있다
    // (실측 — 데스크톱→모바일 전환에서 앵커가 한 칸 밀렸다). 스크롤이 날
    // 때마다(리사이즈 전 마지막 값 포함) 갱신해 두면 전환 핸들러가 그
    // "리사이즈 직전" 값을 그대로 쓸 수 있다.
    scrollState?.onSave({
      anchorId: nextAtNewest ? null : (anchorRef.current?.id ?? null),
      anchorOffset: nextAtNewest ? 0 : (anchorRef.current?.offset ?? 0),
      atNewest: nextAtNewest,
      unseen: liveScrollSnapshotRef.current.unseen,
      seenSeq: seenSeqRef.current,
    })

    // §8.3 — **과거 쪽 끝**에 다다르면 과거분을 지연 로드한다.
    // 최신이 위면 과거는 아래, 최신이 아래면 과거는 위다.
    const distanceFromOldest = newestAtTop ? fromBottom : el.scrollTop
    if (distanceFromOldest < 160 && onLoadMore && !loadingMore && !exhausted) {
      setLoadingMore(true)
      // 위치 보정은 앵커링(useLayoutEffect)이 한다 — 여기서 따로 스냅샷을
      // 뜨지 않는다. 경우별 보정을 병행하면 두 보정이 서로를 덮어쓴다.
      void onLoadMore()
        .then((more) => {
          if (!more) setExhausted(true)
        })
        .catch(() => setExhausted(true))
        .finally(() => setLoadingMore(false))
    }
  }, [onLoadMore, loadingMore, exhausted, captureAnchor, newestAtTop, scrollState])

  if (items.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Empty
          icon={<MessagesSquare className="h-6 w-6" />}
          title={emptyTitle}
          description={emptyDescription}
        />
      </div>
    )
  }

  const virtualItems = virtualizer.getVirtualItems()

  return (
    <div className="relative h-full">
      {/* 로딩 표시는 **과거 쪽 끝**에 뜬다 — 불러오는 자리가 곧 그쪽이다. */}
      {loadingMore && (
        <div
          className={`absolute inset-x-0 z-10 flex justify-center py-2 ${
            newestAtTop ? 'bottom-0' : 'top-0'
          }`}
        >
          <span className="flex items-center gap-2 rounded-full bg-[var(--color-muted)] px-3 py-1 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
            <Spinner className="h-3 w-3" />
            이전 자막 불러오는 중
          </span>
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="feed-scroller h-full px-1"
        data-testid="caption-feed"
        // 스크린리더가 새 자막을 읽도록 — 발표 화면의 접근성.
        // D6-2: 분할 모드에서 두 패널 다 live 면 문단 도착 시 스크린리더가
        // 이미 읽은 문장을 다시 읽는다 — `live` 가 꺼지면 aria-live 자체를 뺀다.
        {...(live ? { 'aria-live': 'polite' as const, 'aria-relevant': 'additions' as const } : {})}
      >
        {showWaiting && newestAtTop && (
          <div className={presentation ? 'pb-4' : 'pb-2'}>
            <LiveWaiting src={src} dst={dst} />
          </div>
        )}
        <div
          style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}
        >
          {virtualItems.map((virtualRow) => {
            const item = items[virtualRow.index]
            if (!item) return null
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                // 스크롤 앵커링의 기준점(§ 위 useLayoutEffect) — 맨 앞 삽입으로
                // index 는 계속 밀리므로 **안정 식별자**로 잡아야 한다.
                data-item-id={item.id}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <div className={presentation ? 'pb-4' : 'pb-2'}>
                  {item.kind === 'fold' ? (
                    <div className="caption-fold" data-testid="caption-fold">
                      통합 대기 — 최근 실시간
                    </div>
                  ) : item.kind === 'prev-divider' ? (
                    <div className="caption-fold" data-testid="prev-meeting-divider">
                      이전 회의
                    </div>
                  ) : (
                    (() => {
                      // D3-1 — 분할 모드(kinds 지정)에서는 정렬 래퍼를 그리지
                      // 않는다. 각 패널이 이미 화면의 한쪽이라 그 안에서 또
                      // 좌/우로 밀면 실사용 폭이 줄어든다. 카드는 전폭으로
                      // 그린다 — 색은 카드 자신이 칠하므로 자동으로 유지된다.
                      const side = kinds ? null : sideOf(item, dst)
                      const card = (
                        <CaptionCard
                          item={item}
                          showSource={showSource}
                          veilSource={veilSource}
                          src={src}
                          dst={dst}
                          {...(item.kind === 'paragraph'
                            ? { members: membersByPara.get(item.id) ?? [], order }
                            : {})}
                          {...(collapseInferred !== undefined
                            ? { collapseInferred }
                            : {})}
                          {...(collapseAssistant !== undefined
                            ? { collapseAssistant }
                            : {})}
                          {...(speakers ? { speakers } : {})}
                          {...(onDelete ? { onDelete } : {})}
                          {...(onRenameSpeaker ? { onRenameSpeaker } : {})}
                          {...(room !== undefined ? { room } : {})}
                          {...(viewerKey !== undefined ? { viewerKey } : {})}
                        />
                      )
                      if (side === null) return card
                      // 정렬 축 = 언어(S21-b): 통역문 좌 / 원어 우.
                      // 크기 위계: 실시간(작다) < 긴호흡 문단(크다).
                      return (
                        <div
                          className={`caption-row caption-row-${side}`}
                          data-side={side}
                        >
                          <div
                            className={
                              item.kind === 'paragraph'
                                ? 'caption-msg-longform'
                                : 'caption-msg-live'
                            }
                          >
                            {card}
                          </div>
                        </div>
                      )
                    })()
                  )}
                </div>
              </div>
            )
          })}
        </div>
        {showWaiting && !newestAtTop && (
          <div className={presentation ? 'pb-4' : 'pb-2'}>
            <LiveWaiting src={src} dst={dst} />
          </div>
        )}
      </div>

      {/* 과거를 읽는 중이면 최신 쪽 끝으로 돌아가는 버튼.
          그 사이 새 자막이 왔으면 **그 사실을 말한다** — 최신이 아래인
          모드에서는 화면 밖에서 자막이 쌓이므로, 버튼이 없으면 새 발화가
          왔는지조차 알 수 없다("조용한 유실 금지" 와 같은 결의 문제다). */}
      {!atNewest && (
        <button
          type="button"
          data-testid="jump-to-newest"
          onClick={() => {
            setAtNewest(true)
            setUnseen(0)
            stickToNewest()
          }}
          className={`absolute left-1/2 z-10 -translate-x-1/2 rounded-full bg-[var(--color-primary)] px-4 py-1.5 text-(length:--text-xs-size) font-medium text-[var(--color-primary-foreground)] shadow-lg ${
            newestAtTop ? 'top-3' : 'bottom-3'
          }`}
        >
          {unseen > 0 ? `새 자막 ${unseen > 99 ? '99+' : unseen}` : '최신 자막으로'}
        </button>
      )}
    </div>
  )
}
